import { captureException } from '@sentry/react';
import IQuizFont from 'models/QuizFont';

const loaded = new Set<string>();

/**
 * Loads fonts returned by the API for the quiz (account fonts and platform fonts).
 * Resolves when all fonts are loaded or after the timeout, so a slow CDN never blocks the quiz.
 */
export const loadQuizFonts = async (fonts: IQuizFont[] = [], timeoutMs = 3000): Promise<void> => {
  const promises = fonts.flatMap(font => font.variants.map(variant => {
    if (loaded.has(variant.href)) return Promise.resolve();
    loaded.add(variant.href);

    const face = new FontFace(font.family, `url("${variant.href}") format("${variant.format}")`, {
      weight: String(variant.weight),
      style: variant.style,
      display: 'swap',
    });
    document.fonts.add(face);
    return face.load().then(() => undefined).catch((error) => {
      loaded.delete(variant.href);
      captureException(error);
    });
  }));

  if (!promises.length) return;

  await Promise.race([
    Promise.allSettled(promises),
    new Promise(resolve => setTimeout(resolve, timeoutMs)),
  ]);
};

/**
 * CSS font-family value for the given family. The fallback is taken from the quiz fonts if the family is one of them.
 */
export const fontStack = (family?: string, fonts: IQuizFont[] = []): string => {
  if (!family) return 'var(--primaryFont), sans-serif';
  const fallback = fonts.find(font => font.family === family)?.fallback ?? 'sans-serif';
  return `"${family}", ${fallback}`;
};
